import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pin, Plus, Trash2, ArrowUp, ArrowDown, Search, AlertTriangle, Clock } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type PinObj = {
  slug: string; title?: string; starts_at?: string; ends_at?: string;
  override_reason?: string; pinned_by?: string; pinned_at?: string;
  health?: string; published?: boolean;
  semantic?: { ok: boolean; reason: string }; quality?: { ok: boolean; reason: string };
  notes?: string[]; _unsaved?: boolean;
};
type SectionCfg = { key: string; label: string; lead_slots?: number; secondary_slots?: number; max_pins?: number };
type SectorCfg = { slug: string; label: string; route: string; family: string; sections: SectionCfg[] };

const HEALTH_STYLE: Record<string, string> = {
  VALID: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  WARNING: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  SCHEDULED: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
  EXPIRED: "text-gray-400 bg-white/5 border-white/15",
  UNPUBLISHED: "text-red-400 bg-red-500/10 border-red-500/30",
  "PENDING SAVE": "text-purple-300 bg-purple-500/10 border-purple-500/30",
};

const HealthChip = ({ status, slug }: { status: string; slug: string }) => (
  <span data-testid={`pin-health-${slug}`}
    className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${HEALTH_STYLE[status] || HEALTH_STYLE.EXPIRED}`}>
    {status}
  </span>
);

const fmtWindow = (p: PinObj) => {
  const parts = [];
  if (p.starts_at) parts.push(`from ${new Date(p.starts_at).toLocaleString()}`);
  if (p.ends_at) parts.push(`until ${new Date(p.ends_at).toLocaleString()}`);
  return parts.join(" ");
};

const PinRow = ({ pin, onRemove, onUp, onDown, testPrefix }: {
  pin: PinObj; onRemove: () => void; onUp?: () => void; onDown?: () => void; testPrefix: string;
}) => (
  <div data-testid={`${testPrefix}-row-${pin.slug}`}
    className="flex items-center gap-2 bg-black/40 border border-white/10 rounded px-3 py-2">
    <Pin size={12} className="text-purple-400 shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-white text-[13px] truncate">{pin.title || pin.slug}</p>
      <p className="font-mono text-[10px] text-gray-500 truncate">
        {pin.slug}{fmtWindow(pin) && <span className="text-cyan-500/80"> · {fmtWindow(pin)}</span>}
        {pin.override_reason && <span className="text-amber-500/80"> · override: {pin.override_reason}</span>}
      </p>
    </div>
    <HealthChip status={pin._unsaved ? "PENDING SAVE" : pin.health || "VALID"} slug={pin.slug} />
    {onUp && <button data-testid={`${testPrefix}-up-${pin.slug}`} onClick={onUp} className="text-gray-500 hover:text-white"><ArrowUp size={13} /></button>}
    {onDown && <button data-testid={`${testPrefix}-down-${pin.slug}`} onClick={onDown} className="text-gray-500 hover:text-white"><ArrowDown size={13} /></button>}
    <button data-testid={`${testPrefix}-remove-${pin.slug}`} onClick={onRemove} className="text-gray-500 hover:text-red-400"><Trash2 size={13} /></button>
  </div>
);

const SlugChips = ({ slugs, onRemove, testPrefix }: { slugs: string[]; onRemove: (s: string) => void; testPrefix: string }) => (
  <div className="flex flex-wrap gap-1.5">
    {slugs.map((s) => (
      <span key={s} data-testid={`${testPrefix}-chip-${s}`}
        className="inline-flex items-center gap-1 font-mono text-[10px] text-gray-300 bg-black/40 border border-white/10 rounded px-2 py-1">
        {s}
        <button data-testid={`${testPrefix}-unchip-${s}`} onClick={() => onRemove(s)} className="text-gray-500 hover:text-red-400">×</button>
      </span>
    ))}
  </div>
);

function PinPicker({ sector, section, open, onClose, onPinned }: {
  sector: string; section: string; open: boolean; onClose: () => void; onPinned: (p: PinObj) => void;
}) {
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState<any[]>([]);
  const [sel, setSel] = useState<any | null>(null);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setQ(""); setSel(null); setReason(""); setStartsAt(""); setEndsAt(""); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/sectors/${sector}/pin-candidates`, { params: { q, section, limit: 15 } });
        setCandidates(r.data.candidates || []);
      } catch (e) { toast.error(apiError(e)); }
    }, 250);
    return () => clearTimeout(t);
  }, [open, q, sector, section]);

  const pick = async (c: any) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/sectors/${sector}/validate-pin`, { slug: c.slug, section });
      if (r.data.hard_block) { toast.error(r.data.hard_reason || "Cannot pin this article"); return; }
      setSel(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const confirm = () => {
    if (sel.requires_override && !reason.trim()) {
      toast.error("An override reason is required to pin outside the semantic gate");
      return;
    }
    const pin: PinObj = { slug: sel.slug, title: sel.title, _unsaved: true };
    if (startsAt) pin.starts_at = new Date(startsAt).toISOString();
    if (endsAt) pin.ends_at = new Date(endsAt).toISOString();
    if (sel.requires_override) pin.override_reason = reason.trim();
    onPinned(pin);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pin-picker-dialog" className="bg-[#121212] border-white/10 text-white max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Pin story — {section === "front" ? "Front Layer" : section}</DialogTitle>
        </DialogHeader>
        {!sel ? (
          <div className="space-y-3">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-2.5 text-gray-500" />
              <Input data-testid="pin-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search all published articles by title…"
                className="bg-black/40 border-white/15 text-white pl-8 text-[13px]" />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
              Gate-qualified stories rank first · out-of-family picks need an override reason
            </p>
            <div className="space-y-1.5 max-h-[46vh] overflow-y-auto pr-1">
              {candidates.map((c, i) => (
                <button key={c.slug} data-testid={`pin-candidate-${i}`} onClick={() => pick(c)} disabled={busy}
                  className="w-full text-left bg-black/40 border border-white/10 rounded px-3 py-2 hover:border-purple-500/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${c.eligible ? "bg-emerald-400" : "bg-amber-400"}`} />
                    <p className="text-[13px] text-white truncate flex-1">{c.title}</p>
                    <span className="font-mono text-[9px] text-gray-500 shrink-0">{timeAgo(c.published_at)}</span>
                  </div>
                  {!c.eligible && <p className="font-mono text-[10px] text-amber-500/80 mt-0.5 pl-3.5 truncate">{c.reason}</p>}
                </button>
              ))}
              {candidates.length === 0 && <p className="text-gray-500 text-sm py-4 text-center">No matching published articles.</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-black/40 border border-white/10 rounded p-3">
              <p className="text-white text-sm">{sel.title}</p>
              <p className="font-mono text-[10px] text-gray-500 mt-0.5">{sel.slug}</p>
            </div>
            {sel.requires_override && (
              <div data-testid="pin-semantic-warning" className="bg-amber-500/10 border border-amber-500/30 rounded p-3 space-y-1">
                <p className="flex items-center gap-1.5 text-amber-400 font-mono text-[10px] uppercase tracking-widest">
                  <AlertTriangle size={12} /> Semantic warning
                </p>
                {!sel.semantic?.ok && <p className="text-amber-200/90 text-xs">{sel.semantic?.reason}</p>}
                {!sel.quality?.ok && <p className="text-amber-200/90 text-xs">{sel.quality?.reason}</p>}
                {(sel.notes || []).map((n: string) => <p key={n} className="text-amber-200/90 text-xs">{n}</p>)}
              </div>
            )}
            {sel.duplicate_in?.length > 0 && (
              <div data-testid="pin-duplicate-warning" className="bg-cyan-500/10 border border-cyan-500/30 rounded p-2.5">
                <p className="text-cyan-300 text-xs">Already pinned in: {sel.duplicate_in.join(", ")} — pinning again duplicates it on the page.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1"><Clock size={10} /> Starts at (optional)</p>
                <Input data-testid="pin-schedule-start" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)}
                  className="bg-black/40 border-white/15 text-white text-xs" />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1"><Clock size={10} /> Pin until (optional)</p>
                <Input data-testid="pin-schedule-end" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)}
                  className="bg-black/40 border-white/15 text-white text-xs" />
              </div>
            </div>
            {sel.requires_override && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Override reason (required)</p>
                <Textarea data-testid="pin-override-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  placeholder="e.g. Major regulatory story materially impacts DeFi despite classifier miss."
                  className="bg-black/40 border-white/15 text-white text-[13px]" />
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setSel(null)} className="text-gray-400">Back</Button>
              <Button data-testid="pin-confirm-btn" onClick={confirm}
                className={sel.requires_override ? "bg-amber-600 hover:bg-amber-700 text-black" : "bg-[#7C3AED] hover:bg-[#6D28D9] text-white"}>
                {sel.requires_override ? "Override warning & pin anyway" : "Pin story"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const emptyState = () => ({
  lead_pin: null as PinObj | null,
  secondary_pins: [] as PinObj[],
  section_pins: {} as Record<string, PinObj[]>,
  suppressed: [] as string[],
  suppressed_sections: {} as Record<string, string[]>,
  trending_override: "",
});

export default function Sectors() {
  const qc = useQueryClient();
  const [sector, setSector] = useState("crypto");
  const [ed, setEd] = useState(emptyState());
  const [picker, setPicker] = useState<{ section: string; target: "lead" | "secondary" | "section" } | null>(null);
  const [supInput, setSupInput] = useState("");
  const [secSupInput, setSecSupInput] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const { data: cfgData } = useQuery({
    queryKey: ["admin-sectors-config"],
    queryFn: async () => (await api.get("/admin/sectors/config")).data,
  });
  const { data } = useQuery({
    queryKey: ["admin-sector-editorial", sector],
    queryFn: async () => (await api.get(`/admin/sectors/${sector}/editorial`)).data,
  });

  const cfg: SectorCfg | undefined = useMemo(
    () => (cfgData?.sectors || []).find((s: SectorCfg) => s.slug === sector), [cfgData, sector]);
  const frontCfg = cfg?.sections.find((s) => s.key === "front");
  const otherSections = (cfg?.sections || []).filter((s) => s.key !== "front");

  useEffect(() => {
    if (!data) return;
    setEd({
      lead_pin: data.lead_pin || null,
      secondary_pins: data.secondary_pins || [],
      section_pins: data.section_pins || {},
      suppressed: data.suppressed || [],
      suppressed_sections: data.suppressed_sections || {},
      trending_override: (data.trending_override || []).join(", "),
    });
  }, [data]);

  const onPinned = (p: PinObj) => {
    if (!picker) return;
    if (picker.target === "lead") setEd({ ...ed, lead_pin: p });
    else if (picker.target === "secondary") setEd({ ...ed, secondary_pins: [...ed.secondary_pins, p] });
    else setEd({ ...ed, section_pins: { ...ed.section_pins, [picker.section]: [...(ed.section_pins[picker.section] || []), p] } });
  };

  const move = (arr: PinObj[], i: number, dir: number) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const out = [...arr];
    [out[i], out[j]] = [out[j], out[i]];
    return out;
  };

  const save = async () => {
    setBusy(true);
    try {
      const strip = (p: PinObj | null) => p && ({ slug: p.slug, starts_at: p.starts_at, ends_at: p.ends_at,
        override_reason: p.override_reason, pinned_by: p.pinned_by, pinned_at: p.pinned_at });
      await api.put(`/admin/sectors/${sector}/editorial`, {
        lead_pin: strip(ed.lead_pin),
        secondary_pins: ed.secondary_pins.map((p) => strip(p)),
        section_pins: Object.fromEntries(Object.entries(ed.section_pins).map(([k, v]) => [k, v.map((p) => strip(p))])),
        suppressed: ed.suppressed,
        suppressed_sections: ed.suppressed_sections,
        trending_override: ed.trending_override.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Editorial controls saved — live within 2 minutes");
      qc.invalidateQueries({ queryKey: ["admin-sector-editorial", sector] });
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      if (d?.warnings?.length) toast.error(`Semantic warning: ${d.warnings[0].semantic?.reason || d.message} — re-pin with an override reason`);
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const addSectionSup = (key: string) => {
    const v = (secSupInput[key] || "").trim();
    if (!v) return;
    const cur = ed.suppressed_sections[key] || [];
    if (!cur.includes(v)) setEd({ ...ed, suppressed_sections: { ...ed.suppressed_sections, [key]: [...cur, v] } });
    setSecSupInput({ ...secSupInput, [key]: "" });
  };

  return (
    <div data-testid="admin-sectors-page">
      <PageHeader title="Sector Editorial Controls"
        sub="Automation by default, editorial override when needed. Pins outrank automation; every unpinned slot stays automated. Overrides are audited." />

      <div className="flex gap-2 mb-5" data-testid="sector-tabs">
        {(cfgData?.sectors || []).map((s: SectorCfg) => (
          <button key={s.slug} data-testid={`sector-tab-${s.slug}`} onClick={() => setSector(s.slug)}
            className={`font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 rounded border transition-colors ${
              sector === s.slug ? "text-white border-purple-500 bg-purple-500/15" : "text-gray-500 border-white/10 hover:text-white"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid gap-5 max-w-3xl">
        {/* FRONT LAYER */}
        <div data-testid="section-card-front" className="bg-[#121212] border border-white/10 rounded-lg p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-mono text-[11px] uppercase tracking-widest text-purple-400">Front Layer</h2>
            <span className="font-mono text-[10px] text-gray-600">{cfg?.route}</span>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Lead story</p>
            {ed.lead_pin ? (
              <PinRow pin={ed.lead_pin} testPrefix="lead" onRemove={() => setEd({ ...ed, lead_pin: null })} />
            ) : (
              <Button data-testid="front-lead-pin-btn" variant="outline" size="sm"
                onClick={() => setPicker({ section: "front", target: "lead" })}
                className="border-white/15 text-gray-400 hover:text-white bg-transparent">
                <Plus size={13} className="mr-1" /> Pin lead — automation picks when empty
              </Button>
            )}
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
              Secondary pins · order = display order · {frontCfg?.secondary_slots || 3} slots, automation fills the rest
            </p>
            <div className="space-y-1.5">
              {ed.secondary_pins.map((p, i) => (
                <PinRow key={p.slug} pin={p} testPrefix="secondary"
                  onRemove={() => setEd({ ...ed, secondary_pins: ed.secondary_pins.filter((x) => x.slug !== p.slug) })}
                  onUp={() => setEd({ ...ed, secondary_pins: move(ed.secondary_pins, i, -1) })}
                  onDown={() => setEd({ ...ed, secondary_pins: move(ed.secondary_pins, i, 1) })} />
              ))}
              {ed.secondary_pins.length < (frontCfg?.secondary_slots || 3) && (
                <Button data-testid="secondary-add-btn" variant="outline" size="sm"
                  onClick={() => setPicker({ section: "front", target: "secondary" })}
                  className="border-white/15 text-gray-400 hover:text-white bg-transparent">
                  <Plus size={13} className="mr-1" /> Pin secondary
                </Button>
              )}
            </div>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Suppress from Front Layer only (slug)</p>
            <SlugChips slugs={ed.suppressed_sections.front || []} testPrefix="sup-front"
              onRemove={(s) => setEd({ ...ed, suppressed_sections: { ...ed.suppressed_sections, front: (ed.suppressed_sections.front || []).filter((x) => x !== s) } })} />
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="section-suppress-input-front" value={secSupInput.front || ""}
                onChange={(e) => setSecSupInput({ ...secSupInput, front: e.target.value })}
                placeholder="article-slug" className="bg-black/40 border-white/15 text-white font-mono text-[12px] h-8" />
              <Button data-testid="section-suppress-add-front" size="sm" variant="outline" onClick={() => addSectionSup("front")}
                className="border-white/15 text-gray-400 hover:text-white bg-transparent h-8">Add</Button>
            </div>
          </div>
        </div>

        {/* SECTION OVERRIDES */}
        {otherSections.map((s) => (
          <div key={s.key} data-testid={`section-card-${s.key}`} className="bg-[#121212] border border-white/10 rounded-lg p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">{s.label}</h2>
              <span className="font-mono text-[10px] text-gray-600">max {s.max_pins} pins</span>
            </div>
            <div className="space-y-1.5">
              {(ed.section_pins[s.key] || []).map((p, i) => (
                <PinRow key={p.slug} pin={p} testPrefix={`sec-${s.key}`}
                  onRemove={() => setEd({ ...ed, section_pins: { ...ed.section_pins, [s.key]: (ed.section_pins[s.key] || []).filter((x) => x.slug !== p.slug) } })}
                  onUp={() => setEd({ ...ed, section_pins: { ...ed.section_pins, [s.key]: move(ed.section_pins[s.key] || [], i, -1) } })}
                  onDown={() => setEd({ ...ed, section_pins: { ...ed.section_pins, [s.key]: move(ed.section_pins[s.key] || [], i, 1) } })} />
              ))}
              {(ed.section_pins[s.key] || []).length < (s.max_pins || 2) && (
                <Button data-testid={`section-add-pin-${s.key}`} variant="outline" size="sm"
                  onClick={() => setPicker({ section: s.key, target: "section" })}
                  className="border-white/15 text-gray-400 hover:text-white bg-transparent">
                  <Plus size={13} className="mr-1" /> Pin into {s.label}
                </Button>
              )}
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Suppress from {s.label} only</p>
              <SlugChips slugs={ed.suppressed_sections[s.key] || []} testPrefix={`sup-${s.key}`}
                onRemove={(x) => setEd({ ...ed, suppressed_sections: { ...ed.suppressed_sections, [s.key]: (ed.suppressed_sections[s.key] || []).filter((y) => y !== x) } })} />
              <div className="flex gap-2 mt-1.5">
                <Input data-testid={`section-suppress-input-${s.key}`} value={secSupInput[s.key] || ""}
                  onChange={(e) => setSecSupInput({ ...secSupInput, [s.key]: e.target.value })}
                  placeholder="article-slug" className="bg-black/40 border-white/15 text-white font-mono text-[12px] h-8" />
                <Button data-testid={`section-suppress-add-${s.key}`} size="sm" variant="outline" onClick={() => addSectionSup(s.key)}
                  className="border-white/15 text-gray-400 hover:text-white bg-transparent h-8">Add</Button>
              </div>
            </div>
          </div>
        ))}

        {/* SECTOR-WIDE SUPPRESSION + TRENDING */}
        <div data-testid="section-card-global" className="bg-[#121212] border border-white/10 rounded-lg p-5 space-y-4">
          <div>
            <h2 className="font-mono text-[11px] uppercase tracking-widest text-red-400 mb-1">Suppressed in {cfg?.label || sector} sector</h2>
            <p className="text-gray-500 text-xs mb-2">Excluded from every {cfg?.label} surface. Other sectors are not affected.</p>
            <SlugChips slugs={ed.suppressed} testPrefix="sup-global"
              onRemove={(s) => setEd({ ...ed, suppressed: ed.suppressed.filter((x) => x !== s) })} />
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="suppressed-input" value={supInput} onChange={(e) => setSupInput(e.target.value)}
                placeholder="article-slug" className="bg-black/40 border-white/15 text-white font-mono text-[12px] h-8" />
              <Button data-testid="suppressed-add" size="sm" variant="outline"
                onClick={() => { const v = supInput.trim(); if (v && !ed.suppressed.includes(v)) setEd({ ...ed, suppressed: [...ed.suppressed, v] }); setSupInput(""); }}
                className="border-white/15 text-gray-400 hover:text-white bg-transparent h-8">Add</Button>
            </div>
          </div>
          <div>
            <h2 className="font-mono text-[11px] uppercase tracking-widest text-cyan-400 mb-1">Trending override</h2>
            <p className="text-gray-500 text-xs mb-2">Pins topic keys (e.g. bitcoin, regulation) to the front of Trending. Counts stay real.</p>
            <Input data-testid="sector-trending-override" value={ed.trending_override}
              onChange={(e) => setEd({ ...ed, trending_override: e.target.value })}
              className="bg-black/40 border-white/15 text-white font-mono text-[13px]" />
          </div>
        </div>

        <div className="flex items-center justify-between pb-8">
          {data?.updated_at && (
            <span className="font-mono text-[11px] text-gray-500">Last saved {timeAgo(data.updated_at)} by {data.updated_by || "—"}</span>
          )}
          <Button data-testid="sector-editorial-save" onClick={save} disabled={busy}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white ml-auto">
            {busy ? "Saving…" : "Save controls"}
          </Button>
        </div>
      </div>

      {picker && (
        <PinPicker sector={sector} section={picker.section} open onClose={() => setPicker(null)} onPinned={onPinned} />
      )}
    </div>
  );
}
